import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { resolveUrl as resolveCoverUrl } from "@/components/CoverUpload";
import { toast } from "sonner";
import { Users, ChevronRight } from "lucide-react";

export default function InviteLanding() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setData(data);
        setStatus("ok");
      } catch (e) {
        setStatus("notfound");
      }
    })();
  }, [token]);

  const accept = async () => {
    if (!user) {
      // Bounce through login, come back here
      sessionStorage.setItem("bf_pending_invite", token);
      navigate(`/login?next=/invites/${token}`);
      return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/invites/${token}/accept`);
      toast.success(r.already_accepted ? "Already accepted earlier" : "Joined the book — you're now a collaborator");
      navigate(`/books/${r.book_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept");
    } finally {
      setBusy(false);
    }
  };

  if (loading || status === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-sm" data-testid="invite-loading">Loading invite…</div>;
  }
  if (status === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="invite-notfound">
        <div className="text-center">
          <h1 className="font-serif text-5xl mb-3">Invite not found</h1>
          <p className="mb-6" style={{ color: "var(--bf-text-muted)" }}>This invite may have been revoked.</p>
          <Link to="/"><Button variant="outline">Back home</Button></Link>
        </div>
      </div>
    );
  }

  const { invite, book, owner } = data;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: "var(--bf-bg)" }} data-testid="invite-page">
      <div className="w-full max-w-lg p-10 rounded-lg" style={{ background: "white", border: "1px solid var(--bf-border)" }}>
        <Users className="w-8 h-8 mb-4" style={{ color: "var(--bf-primary)" }} />
        <div className="label-eyebrow mb-3">You've been invited</div>
        <h1 className="font-serif text-3xl mb-2">Join <em style={{ color: "var(--bf-primary)" }}>{book?.title || "this book"}</em></h1>
        <p className="text-sm mb-8" style={{ color: "var(--bf-text-muted)" }}>
          {owner?.name ? `${owner.name} invited you ` : ""}as <strong>{invite.role}</strong>.{" "}
          {invite.role === "editor" ? "You'll be able to read and edit chapters." : "You'll be able to read chapters."}
        </p>

        {book?.cover_image_url && (
          <img
            src={resolveCoverUrl(book.cover_image_url)}
            alt="cover"
            className="w-32 aspect-[2/3] object-cover rounded mb-6"
            style={{ border: "1px solid var(--bf-border)" }}
          />
        )}

        {invite.status === "accepted" ? (
          <Button
            onClick={() => navigate(`/books/${book.book_id}`)}
            className="w-full text-white h-11"
            style={{ background: "var(--bf-primary)" }}
            data-testid="invite-open-book-btn"
          >
            Open the book <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : !user ? (
          <div className="space-y-3" data-testid="invite-needs-login">
            <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
              Sign in or create an account with <strong>{invite.invitee_email}</strong> to accept.
            </p>
            <Button
              onClick={() => navigate(`/login?next=/invites/${token}`)}
              className="w-full text-white h-11"
              style={{ background: "var(--bf-primary)" }}
              data-testid="invite-login-btn"
            >
              Sign in to accept
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/register?next=/invites/${token}`)}
              className="w-full h-11"
              data-testid="invite-register-btn"
            >
              Create an account
            </Button>
          </div>
        ) : (
          <Button
            onClick={accept}
            disabled={busy}
            className="w-full text-white h-11"
            style={{ background: "var(--bf-primary)" }}
            data-testid="invite-accept-btn"
          >
            {busy ? "Accepting…" : "Accept invite"}
          </Button>
        )}
      </div>
    </div>
  );
}
